import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { sortRows, SortableTh, toggleSort } from '../utils/sortTable';

const statusOptions = [
  { value: 'draft', label: 'Taslak' },
  { value: 'quoting', label: 'Teklif Alınıyor' },
  { value: 'done', label: 'Tamamlandı' },
];

const emptyAddAssembly = { assembly_product_id: '', quantity: 1 };
const emptyQuantity = { project_assembly_id: null, quantity: '' };
const emptyQuote = { supplier: '', unit_price: '', currency: 'USD', lead_time_days: '', moq: '', quoted_date: '', notes: '', is_selected: false };

function money(value, currency, decimals = 2) {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(decimals)} ${currency || ''}`.trim();
}

function moneyByCurrency(byCurrency) {
  const entries = Object.entries(byCurrency || {});
  if (entries.length === 0) return '-';
  return entries.map(([currency, value]) => money(value, currency)).join(' + ');
}

export default function SourcingProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [assemblies, setAssemblies] = useState([]);
  const [showAddAssembly, setShowAddAssembly] = useState(false);
  const [addAssemblyForm, setAddAssemblyForm] = useState(emptyAddAssembly);
  const [showQuantityForm, setShowQuantityForm] = useState(false);
  const [quantityForm, setQuantityForm] = useState(emptyQuantity);
  const [reExploding, setReExploding] = useState(false);

  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [activeLineItemId, setActiveLineItemId] = useState(null);
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [quoteForm, setQuoteForm] = useState(emptyQuote);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef();

  const [selectedLineItemIds, setSelectedLineItemIds] = useState(new Set());
  const [movingToPreOrder, setMovingToPreOrder] = useState(false);
  const [selectedPreOrderIds, setSelectedPreOrderIds] = useState(new Set());
  const [showPoModal, setShowPoModal] = useState(false);
  const [poForm, setPoForm] = useState({ supplier: '', to: '', message: '' });
  const [sendingPo, setSendingPo] = useState(false);
  const [poSelectedKeys, setPoSelectedKeys] = useState(new Set());
  const [splitLineItemId, setSplitLineItemId] = useState(null);
  const [splitRows, setSplitRows] = useState({});
  const [savingSplit, setSavingSplit] = useState(false);
  const [quotingSort, setQuotingSort] = useState({ key: null, dir: 'asc' });
  const [preOrderSort, setPreOrderSort] = useState({ key: null, dir: 'asc' });

  const fetchProject = useCallback(async () => {
    const res = await api.get(`/api/sourcing/projects/${id}`);
    setProject(res.data);
  }, [id]);

  const fetchAssemblies = async () => {
    const res = await api.get('/api/bom/assemblies');
    setAssemblies(res.data);
  };

  useEffect(() => { fetchProject(); fetchAssemblies(); }, [fetchProject]);

  const activeLineItem = project?.line_items.find(li => li.id === activeLineItemId) || null;
  const usedAssemblyIds = new Set((project?.assemblies || []).map(a => a.assembly_product_id));
  const assemblyCandidates = assemblies.filter(a => !usedAssemblyIds.has(a.id));
  const quotingLineItems = (project?.line_items || []).filter(li => li.status !== 'pre_order');
  const preOrderRows = (project?.line_items || [])
    .filter(li => li.status === 'pre_order')
    .flatMap(li => (li.allocations.length > 0 ? li.allocations : [null]).map(a => ({
      ...li,
      rowKey: `${li.id}:${a ? a.quote_id : 'none'}`,
      quote_id: a ? a.quote_id : null,
      quantity_required: a ? a.quantity : li.quantity_required,
      total_quantity: li.quantity_required,
      supplier: a?.supplier || null,
      moq: a?.moq ?? null,
      best_unit_price: a?.unit_price ?? null,
      best_currency: a?.currency ?? null,
      best_lead_time_days: a?.lead_time_days ?? null,
    })));
  const splitLineItem = project?.line_items.find(li => li.id === splitLineItemId) || null;
  const sortedQuotingItems = sortRows(quotingLineItems, quotingSort);
  const sortedPreOrderRows = sortRows(preOrderRows, preOrderSort);

  const toggleQuotingSort = (key) => toggleSort(setQuotingSort, key);
  const togglePreOrderSort = (key) => toggleSort(setPreOrderSort, key);

  const handleStatusChange = async (status) => {
    try {
      await api.put(`/api/sourcing/projects/${id}`, { status });
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleAddAssemblySubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/sourcing/projects/${id}/assemblies`, {
        assembly_product_id: parseInt(addAssemblyForm.assembly_product_id),
        quantity: parseFloat(addAssemblyForm.quantity),
      });
      setShowAddAssembly(false);
      setAddAssemblyForm(emptyAddAssembly);
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const openQuantityForm = (projectAssembly) => {
    setQuantityForm({ project_assembly_id: projectAssembly.id, quantity: projectAssembly.quantity });
    setShowQuantityForm(true);
  };

  const handleQuantitySubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/sourcing/projects/${id}/assemblies/${quantityForm.project_assembly_id}`, {
        quantity: parseFloat(quantityForm.quantity),
      });
      setShowQuantityForm(false);
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleRemoveAssembly = async (projectAssemblyId) => {
    if (!window.confirm('Bu montajı projeden çıkarmak istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/api/sourcing/projects/${id}/assemblies/${projectAssemblyId}`);
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleReExplode = async () => {
    setReExploding(true);
    try {
      await api.post(`/api/sourcing/projects/${id}/re-explode`);
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setReExploding(false);
    }
  };

  const handleExcelDownload = async () => {
    const res = await api.get(`/api/sourcing/projects/${id}/excel`, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(new Blob([res.data]));
    a.download = `tedarik_${id}.xlsx`;
    a.click();
  };

  const handleStockReportDownload = async () => {
    try {
      const res = await api.get(`/api/sourcing/projects/${id}/stock-report/excel`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(new Blob([res.data]));
      a.download = `stok_karsilastirma_${id}.xlsx`;
      a.click();
    } catch (err) {
      alert('Rapor oluşturulamadı');
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/api/sourcing/projects/${id}/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      fetchProject();
    } catch (err) {
      alert('İçe aktarma hatası: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setImporting(false);
      importFileRef.current.value = '';
    }
  };

  const toggleLineItemSelected = (lineItemId) => {
    setSelectedLineItemIds(prev => {
      const next = new Set(prev);
      if (next.has(lineItemId)) next.delete(lineItemId); else next.add(lineItemId);
      return next;
    });
  };

  const toggleSelectAllQuoting = () => {
    setSelectedLineItemIds(prev => {
      const allSelected = quotingLineItems.length > 0 && quotingLineItems.every(li => prev.has(li.id));
      return allSelected ? new Set() : new Set(quotingLineItems.map(li => li.id));
    });
  };

  const handleMoveToPreOrder = async () => {
    if (selectedLineItemIds.size === 0) return;
    setMovingToPreOrder(true);
    try {
      await api.post(`/api/sourcing/projects/${id}/line-items/move-to-pre-order`, {
        line_item_ids: Array.from(selectedLineItemIds),
      });
      setSelectedLineItemIds(new Set());
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setMovingToPreOrder(false);
    }
  };

  const openSplitModal = (lineItem) => {
    const rows = {};
    lineItem.allocations.forEach(a => { rows[a.quote_id] = String(a.quantity); });
    setSplitRows(rows);
    setSplitLineItemId(lineItem.id);
  };

  const splitTotal = Object.values(splitRows).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);

  const handleSplitSubmit = async (e) => {
    e.preventDefault();
    const allocations = Object.entries(splitRows)
      .map(([quoteId, qty]) => ({ quote_id: parseInt(quoteId), quantity: parseFloat(qty) || 0 }))
      .filter(a => a.quantity > 0);
    setSavingSplit(true);
    try {
      await api.put(`/api/sourcing/projects/${id}/line-items/${splitLineItemId}/allocations`, { allocations });
      setSplitLineItemId(null);
      setSelectedPreOrderIds(new Set());
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setSavingSplit(false);
    }
  };

  const handleMoveToQuoting = async (lineItemId) => {
    try {
      await api.post(`/api/sourcing/projects/${id}/line-items/move-to-quoting`, {
        line_item_ids: [lineItemId],
      });
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const togglePreOrderSelected = (lineItemId) => {
    setSelectedPreOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(lineItemId)) next.delete(lineItemId); else next.add(lineItemId);
      return next;
    });
  };

  const toggleSelectAllPreOrder = () => {
    setSelectedPreOrderIds(prev => {
      const allSelected = preOrderRows.length > 0 && preOrderRows.every(li => prev.has(li.rowKey));
      return allSelected ? new Set() : new Set(preOrderRows.map(li => li.rowKey));
    });
  };

  const handlePreOrderExcelDownload = async (onlySelected) => {
    const params = {};
    if (onlySelected) {
      if (selectedPreOrderIds.size === 0) return;
      const rows = preOrderRows.filter(r => selectedPreOrderIds.has(r.rowKey));
      params.line_item_ids = Array.from(new Set(rows.map(r => r.id))).join(',');
      params.quote_ids = rows.map(r => r.quote_id).filter(Boolean).join(',');
    }
    const res = await api.get(`/api/sourcing/projects/${id}/pre-order/excel`, { params, responseType: 'blob' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(new Blob([res.data]));
    a.download = `on_siparis_${id}.xlsx`;
    a.click();
  };

  const sameVendor = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
  const poVendors = Array.from(new Set(preOrderRows.map(r => r.supplier).filter(Boolean)));
  const poVendorRows = poForm.supplier.trim() ? preOrderRows.filter(r => sameVendor(r.supplier, poForm.supplier)) : [];

  // Rows of a vendor that start checked in the PO modal: the ones selected in the
  // pre-order table, or all of the vendor's rows when nothing is selected there.
  const poDefaultKeys = (vendor) => {
    if (!vendor.trim()) return new Set();
    const vendorRows = preOrderRows.filter(r => sameVendor(r.supplier, vendor));
    const picked = selectedPreOrderIds.size > 0 ? vendorRows.filter(r => selectedPreOrderIds.has(r.rowKey)) : vendorRows;
    return new Set(picked.map(r => r.rowKey));
  };

  const handlePoVendorChange = (value) => {
    setPoForm(prev => ({ ...prev, supplier: value }));
    setPoSelectedKeys(poDefaultKeys(value));
  };

  // Mirrors the backend: a PO orders at least the vendor's MOQ.
  const poOrderQuantity = (r) => Math.max(r.quantity_required, r.moq || 0);

  const togglePoRow = (rowKey) => {
    setPoSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
      return next;
    });
  };

  const openPoModal = () => {
    // Prefill the vendor when every selected pre-order row belongs to the same one.
    const selectedVendors = Array.from(new Set(
      preOrderRows.filter(r => selectedPreOrderIds.has(r.rowKey)).map(r => (r.supplier || '').trim())
    ));
    const supplier = selectedVendors.length === 1 ? selectedVendors[0] : '';
    setPoForm({ supplier, to: '', message: '' });
    setPoSelectedKeys(poDefaultKeys(supplier));
    setShowPoModal(true);
  };

  const handleSendPo = async (e) => {
    e.preventDefault();
    setSendingPo(true);
    try {
      const rows = poVendorRows.filter(r => poSelectedKeys.has(r.rowKey));
      const payload = {
        line_item_ids: Array.from(new Set(rows.map(r => r.id))),
        quote_ids: rows.map(r => r.quote_id).filter(Boolean),
        supplier: poForm.supplier.trim(),
        to: poForm.to,
      };
      if (poForm.message) payload.message = poForm.message;
      const res = await api.post(`/api/sourcing/projects/${id}/purchase-order/send-email`, payload);
      alert(res.data.message);
      setShowPoModal(false);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'E-posta gönderilemedi'));
    } finally {
      setSendingPo(false);
    }
  };

  const openQuoteModal = (lineItem) => {
    setActiveLineItemId(lineItem.id);
    setEditingQuoteId(null);
    setQuoteForm(emptyQuote);
    setShowQuoteModal(true);
  };

  const openEditQuote = (quote) => {
    setEditingQuoteId(quote.id);
    setQuoteForm({
      supplier: quote.supplier || '',
      unit_price: quote.unit_price,
      currency: quote.currency || 'USD',
      lead_time_days: quote.lead_time_days ?? '',
      moq: quote.moq ?? '',
      quoted_date: quote.quoted_date || '',
      notes: quote.notes || '',
      is_selected: quote.is_selected,
    });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...quoteForm,
        unit_price: parseFloat(quoteForm.unit_price),
        lead_time_days: quoteForm.lead_time_days === '' ? null : parseInt(quoteForm.lead_time_days),
        moq: quoteForm.moq === '' ? null : parseFloat(quoteForm.moq),
        quoted_date: quoteForm.quoted_date || null,
      };
      if (editingQuoteId) {
        await api.put(`/api/sourcing/projects/${id}/line-items/${activeLineItemId}/quotes/${editingQuoteId}`, payload);
      } else {
        await api.post(`/api/sourcing/projects/${id}/line-items/${activeLineItemId}/quotes`, payload);
      }
      setEditingQuoteId(null);
      setQuoteForm(emptyQuote);
      fetchProject();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleQuoteDelete = async (quoteId) => {
    if (!window.confirm('Teklifi silmek istediğinize emin misiniz?')) return;
    await api.delete(`/api/sourcing/projects/${id}/line-items/${activeLineItemId}/quotes/${quoteId}`);
    fetchProject();
  };

  const handleQuoteSelect = async (quoteId) => {
    await api.put(`/api/sourcing/projects/${id}/line-items/${activeLineItemId}/quotes/${quoteId}`, { is_selected: true });
    fetchProject();
  };

  if (!project) return null;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Link to="/sourcing" className="text-blue-600 hover:text-blue-800 text-sm">← Tedarik Projeleri</Link>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{project.name}</h2>
          {project.notes && <p className="text-sm text-gray-500 mt-1">{project.notes}</p>}
        </div>
        <select value={project.status} onChange={e => handleStatusChange(e.target.value)} className="border rounded px-3 py-2 text-sm">
          {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500">Toplam Maliyet</p>
          <p className="text-xl font-bold text-gray-800">{moneyByCurrency(project.total_cost_by_currency)}</p>
          <p className="text-xs text-gray-400 mt-1">{project.priced_line_item_count}/{project.line_item_count} kalem fiyatlandırıldı</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500">Darboğaz Termin</p>
          <p className="text-xl font-bold text-gray-800">{project.bottleneck_lead_time_days ?? '-'} gün</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 flex items-center">
          <button onClick={handleReExplode} disabled={reExploding} className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium text-sm disabled:opacity-50">
            {reExploding ? 'Yeniden Patlatılıyor...' : '🔄 Yeniden Patlat'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Montajlar</h3>
          <button onClick={() => { setAddAssemblyForm(emptyAddAssembly); setShowAddAssembly(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">
            + Montaj Ekle
          </button>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-2 text-sm font-medium text-gray-500">Kod</th>
              <th className="text-left px-4 py-2 text-sm font-medium text-gray-500">Ad</th>
              <th className="text-right px-4 py-2 text-sm font-medium text-gray-500">Miktar</th>
              <th className="text-right px-4 py-2 text-sm font-medium text-gray-500">Bileşen Maliyeti</th>
              <th className="text-right px-4 py-2 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {project.assemblies.map(a => (
              <tr key={a.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-mono text-gray-600">{a.assembly_code}</td>
                <td className="px-4 py-2 font-medium text-gray-800">
                  <Link to={`/assemblies/${a.assembly_product_id}`} className="hover:text-blue-600">{a.assembly_name}</Link>
                </td>
                <td className="px-4 py-2 text-right text-gray-800">{a.quantity}</td>
                <td className="px-4 py-2 text-right text-gray-800">{moneyByCurrency(a.total_cost_by_currency)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => openQuantityForm(a)} className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-2">Miktar Düzenle</button>
                  <button onClick={() => handleRemoveAssembly(a.id)} className="text-red-500 hover:text-red-700 font-medium text-sm">Çıkar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Kalemler (Birleşik BOM)</h3>
          <div className="flex gap-2">
            <button onClick={handleMoveToPreOrder} disabled={selectedLineItemIds.size === 0 || movingToPreOrder} className="bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 text-sm font-medium disabled:opacity-50">
              {movingToPreOrder ? 'Taşınıyor...' : `🛒 Ön Sipariş Listesine Taşı${selectedLineItemIds.size > 0 ? ` (${selectedLineItemIds.size})` : ''}`}
            </button>
            <button onClick={handleExcelDownload} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 text-sm font-medium">
              📥 Excel İndir
            </button>
            <button onClick={handleStockReportDownload} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 text-sm font-medium">
              📦 Stok Karşılaştırma Raporu
            </button>
            <button onClick={() => importFileRef.current.click()} disabled={importing} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 text-sm font-medium disabled:opacity-50">
              {importing ? 'Yükleniyor...' : '⬆️ Excel Yükle (Teklifler)'}
            </button>
            <input ref={importFileRef} type="file" accept=".xlsx,.xls" onChange={handleExcelImport} className="hidden" />
          </div>
        </div>

        {importResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-green-700 font-medium">{importResult.message}</p>
            {importResult.errors && importResult.errors.length > 0 && (
              <ul className="mt-2 text-red-600 text-sm">
                {importResult.errors.map((e, i) => <li key={i}>⚠️ {e}</li>)}
              </ul>
            )}
            <button onClick={() => setImportResult(null)} className="mt-2 text-sm text-gray-500 hover:text-gray-700">Kapat</button>
          </div>
        )}

        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-2 w-8">
                <input type="checkbox" checked={quotingLineItems.length > 0 && quotingLineItems.every(li => selectedLineItemIds.has(li.id))} onChange={toggleSelectAllQuoting} />
              </th>
              <SortableTh label="Kod" sortKey="product_code" sort={quotingSort} onSort={toggleQuotingSort} />
              <SortableTh label="Ad" sortKey="product_name" sort={quotingSort} onSort={toggleQuotingSort} />
              <SortableTh label="Ozdisan Kodu" sortKey="ozdisan_code" sort={quotingSort} onSort={toggleQuotingSort} />
              <SortableTh label="Gerekli Miktar" sortKey="quantity_required" sort={quotingSort} onSort={toggleQuotingSort} align="right" />
              <SortableTh label="En İyi Fiyat" sortKey="best_unit_price" sort={quotingSort} onSort={toggleQuotingSort} align="right" />
              <SortableTh label="Termin" sortKey="best_lead_time_days" sort={quotingSort} onSort={toggleQuotingSort} align="right" />
              <th className="text-right px-4 py-2 text-sm font-medium text-gray-500">Teklifler</th>
              <th className="text-right px-4 py-2 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedQuotingItems.map(li => (
              <tr key={li.id} className="hover:bg-gray-50">
                <td className="px-4 py-2">
                  <input type="checkbox" checked={selectedLineItemIds.has(li.id)} onChange={() => toggleLineItemSelected(li.id)} />
                </td>
                <td className="px-4 py-2 text-sm font-mono text-gray-600">{li.product_code}</td>
                <td className="px-4 py-2 font-medium text-gray-800">{li.product_name}</td>
                <td className="px-4 py-2 text-sm font-mono text-gray-600">{li.ozdisan_code || '-'}</td>
                <td className="px-4 py-2 text-right text-gray-800">{li.quantity_required} {li.unit}</td>
                <td className="px-4 py-2 text-right text-gray-800">{money(li.best_unit_price, li.best_currency, 4)}</td>
                <td className="px-4 py-2 text-right text-gray-600">{li.best_lead_time_days ?? '-'}</td>
                <td className="px-4 py-2 text-right text-gray-600">{li.quotes.length}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => openQuoteModal(li)} className="text-blue-600 hover:text-blue-800 font-medium text-sm">Teklifler</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {quotingLineItems.length === 0 && (
          <div className="text-center py-8 text-gray-400">{preOrderRows.length > 0 ? 'Tüm kalemler ön sipariş listesine taşındı' : 'Bu proje için bileşen bulunamadı'}</div>
        )}
      </div>

      {preOrderRows.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">🛒 Ön Sipariş Listesi</h3>
            <div className="flex gap-2">
              <button onClick={openPoModal} className="bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 text-sm font-medium">
                ✉️ PO Gönder
              </button>
              <button onClick={() => handlePreOrderExcelDownload(true)} disabled={selectedPreOrderIds.size === 0} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 text-sm font-medium disabled:opacity-50">
                📥 Seçilenleri İndir{selectedPreOrderIds.size > 0 ? ` (${selectedPreOrderIds.size})` : ''}
              </button>
              <button onClick={() => handlePreOrderExcelDownload(false)} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 text-sm font-medium">
                📥 Tümünü İndir
              </button>
            </div>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2 w-8">
                  <input type="checkbox" checked={preOrderRows.length > 0 && preOrderRows.every(li => selectedPreOrderIds.has(li.rowKey))} onChange={toggleSelectAllPreOrder} />
                </th>
                <SortableTh label="Kod" sortKey="product_code" sort={preOrderSort} onSort={togglePreOrderSort} />
                <SortableTh label="Ad" sortKey="product_name" sort={preOrderSort} onSort={togglePreOrderSort} />
                <SortableTh label="Ozdisan Kodu" sortKey="ozdisan_code" sort={preOrderSort} onSort={togglePreOrderSort} />
                <SortableTh label="Miktar" sortKey="quantity_required" sort={preOrderSort} onSort={togglePreOrderSort} align="right" />
                <SortableTh label="Tedarikçi" sortKey="supplier" sort={preOrderSort} onSort={togglePreOrderSort} />
                <SortableTh label="Fiyat" sortKey="best_unit_price" sort={preOrderSort} onSort={togglePreOrderSort} align="right" />
                <SortableTh label="Termin" sortKey="best_lead_time_days" sort={preOrderSort} onSort={togglePreOrderSort} align="right" />
                <SortableTh label="MOQ" sortKey="moq" sort={preOrderSort} onSort={togglePreOrderSort} align="right" />
                <th className="text-right px-4 py-2 text-sm font-medium text-gray-500">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedPreOrderRows.map(li => (
                <tr key={li.rowKey} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <input type="checkbox" checked={selectedPreOrderIds.has(li.rowKey)} onChange={() => togglePreOrderSelected(li.rowKey)} />
                  </td>
                  <td className="px-4 py-2 text-sm font-mono text-gray-600">{li.product_code}</td>
                  <td className="px-4 py-2 font-medium text-gray-800">{li.product_name}</td>
                  <td className="px-4 py-2 text-sm font-mono text-gray-600">{li.ozdisan_code || '-'}</td>
                  <td className="px-4 py-2 text-right text-gray-800">{li.quantity_required} {li.unit}{li.is_split && <span className="text-xs text-gray-400"> / {li.total_quantity}</span>}</td>
                  <td className="px-4 py-2 text-gray-800">{li.supplier || '-'}</td>
                  <td className="px-4 py-2 text-right text-gray-800">{money(li.best_unit_price, li.best_currency, 4)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{li.best_lead_time_days ?? '-'}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{li.moq ?? '-'}</td>
                  <td className="px-4 py-2 text-right">
                    {li.quotes.length > 0 && (
                      <button onClick={() => openSplitModal(li)} className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-3">✂ Böl</button>
                    )}
                    <button onClick={() => handleMoveToQuoting(li.id)} className="text-gray-500 hover:text-gray-700 font-medium text-sm">↩ Geri Taşı</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddAssembly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Montaj Ekle</h3>
            <form onSubmit={handleAddAssemblySubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montaj *</label>
                <select required value={addAssemblyForm.assembly_product_id} onChange={e => setAddAssemblyForm({...addAssemblyForm, assembly_product_id: e.target.value})} className="w-full border rounded px-3 py-2">
                  <option value="">Seçiniz</option>
                  {assemblyCandidates.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                </select>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Miktar *</label>
                <input required type="number" step="0.001" min="0.001" value={addAssemblyForm.quantity} onChange={e => setAddAssemblyForm({...addAssemblyForm, quantity: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Ekle</button>
                <button type="button" onClick={() => setShowAddAssembly(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showQuantityForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">Miktar Düzenle</h3>
            <form onSubmit={handleQuantitySubmit}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Miktar *</label>
              <input required type="number" step="0.001" min="0.001" value={quantityForm.quantity} onChange={e => setQuantityForm({...quantityForm, quantity: e.target.value})} className="w-full border rounded px-3 py-2" />
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Kaydet</button>
                <button type="button" onClick={() => setShowQuantityForm(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showQuoteModal && activeLineItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-1">Tedarikçi Teklifleri</h3>
            <p className="text-sm text-gray-500 mb-4">{activeLineItem.product_code} - {activeLineItem.product_name} (gerekli: {activeLineItem.quantity_required} {activeLineItem.unit})</p>

            <table className="w-full mb-4">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Tedarikçi</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Fiyat</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Termin</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">MOQ</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Tarih</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {activeLineItem.quotes.map(q => (
                  <tr key={q.id} className={q.is_selected ? 'bg-green-50' : 'hover:bg-gray-50'}>
                    <td className="px-3 py-2 text-sm text-gray-800">{q.supplier || '-'} {q.is_selected && <span className="text-green-600 text-xs font-medium ml-1">✓ Seçili</span>}</td>
                    <td className="px-3 py-2 text-right text-sm text-gray-800">{money(q.unit_price, q.currency, 4)}</td>
                    <td className="px-3 py-2 text-right text-sm text-gray-600">{q.lead_time_days ?? '-'}</td>
                    <td className="px-3 py-2 text-right text-sm text-gray-600">{q.moq ?? '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{q.quoted_date || '-'}</td>
                    <td className="px-3 py-2 text-right">
                      {!q.is_selected && <button onClick={() => handleQuoteSelect(q.id)} className="text-green-600 hover:text-green-800 text-xs font-medium mr-2">Seç</button>}
                      <button onClick={() => openEditQuote(q)} className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-2">Düzenle</button>
                      <button onClick={() => handleQuoteDelete(q.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {activeLineItem.quotes.length === 0 && (
              <p className="text-gray-400 text-sm text-center mb-4">Henüz teklif eklenmemiş</p>
            )}

            <h4 className="font-semibold text-sm text-gray-700 mb-2">{editingQuoteId ? 'Teklifi Düzenle' : 'Yeni Teklif Ekle'}</h4>
            <form onSubmit={handleQuoteSubmit}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tedarikçi</label>
                  <input value={quoteForm.supplier} onChange={e => setQuoteForm({...quoteForm, supplier: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Birim Fiyat *</label>
                  <input required type="number" step="0.0001" min="0" value={quoteForm.unit_price} onChange={e => setQuoteForm({...quoteForm, unit_price: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Para Birimi</label>
                  <select value={quoteForm.currency} onChange={e => setQuoteForm({...quoteForm, currency: e.target.value})} className="w-full border rounded px-3 py-2 text-sm">
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Termin (gün)</label>
                  <input type="number" min="0" value={quoteForm.lead_time_days} onChange={e => setQuoteForm({...quoteForm, lead_time_days: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">MOQ</label>
                  <input type="number" step="0.001" min="0" value={quoteForm.moq} onChange={e => setQuoteForm({...quoteForm, moq: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Teklif Tarihi</label>
                  <input type="date" value={quoteForm.quoted_date} onChange={e => setQuoteForm({...quoteForm, quoted_date: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Not</label>
                  <textarea value={quoteForm.notes} onChange={e => setQuoteForm({...quoteForm, notes: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" rows={2} />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">{editingQuoteId ? 'Güncelle' : 'Ekle'}</button>
                {editingQuoteId && (
                  <button type="button" onClick={() => { setEditingQuoteId(null); setQuoteForm(emptyQuote); }} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium text-sm">Vazgeç</button>
                )}
              </div>
            </form>

            <button onClick={() => setShowQuoteModal(false)} className="mt-4 w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium text-sm">Kapat</button>
          </div>
        </div>
      )}

      {splitLineItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-1">Ön Siparişi Böl</h3>
            <p className="text-sm text-gray-500 mb-4">{splitLineItem.product_code} - {splitLineItem.product_name} · Toplam miktar: {splitLineItem.quantity_required} {splitLineItem.unit}</p>
            <form onSubmit={handleSplitSubmit}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-gray-500">Tedarikçi</th>
                    <th className="text-right px-3 py-2 text-gray-500">Fiyat</th>
                    <th className="text-right px-3 py-2 text-gray-500">Termin</th>
                    <th className="text-right px-3 py-2 text-gray-500">MOQ</th>
                    <th className="text-right px-3 py-2 text-gray-500 w-32">Miktar</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {splitLineItem.quotes.map(q => (
                    <tr key={q.id}>
                      <td className="px-3 py-2 text-gray-800">{q.supplier || '-'}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{money(q.unit_price, q.currency, 4)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{q.lead_time_days ?? '-'}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{q.moq ?? '-'}</td>
                      <td className="px-3 py-2">
                        <input type="number" step="any" min="0" value={splitRows[q.id] ?? ''} onChange={e => setSplitRows({...splitRows, [q.id]: e.target.value})} className="w-full border rounded px-2 py-1 text-right" placeholder="0" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className={`text-sm mt-3 ${Math.abs(splitTotal - splitLineItem.quantity_required) < 1e-6 ? 'text-green-600' : 'text-red-600'}`}>
                Dağıtılan: {splitTotal} / {splitLineItem.quantity_required} {splitLineItem.unit}
              </p>
              <div className="flex gap-3 mt-6">
                <button type="submit" disabled={savingSplit || (splitTotal > 0 && Math.abs(splitTotal - splitLineItem.quantity_required) > 1e-6)} className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">Kaydet</button>
                <button type="button" onClick={() => setSplitRows({})} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 font-medium">Sıfırla</button>
                <button type="button" onClick={() => setSplitLineItemId(null)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Satın Alma Siparişi Gönder</h3>
            <form onSubmit={handleSendPo}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tedarikçi *</label>
                <input required list="po-vendors" value={poForm.supplier} onChange={e => handlePoVendorChange(e.target.value)} className="w-full border rounded px-3 py-2" placeholder="Tedarikçi adı yazın veya seçin" autoFocus />
                <datalist id="po-vendors">
                  {poVendors.map(v => <option key={v} value={v} />)}
                </datalist>
              </div>
              {poForm.supplier.trim() && (
                poVendorRows.length === 0 ? (
                  <p className="text-sm text-red-600 mt-3">Bu tedarikçiye ait ön sipariş kalemi bulunamadı</p>
                ) : (
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-2">{poSelectedKeys.size} / {poVendorRows.length} kalem seçili</p>
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 w-8"></th>
                          <th className="text-left px-3 py-2 text-gray-500">Kod</th>
                          <th className="text-left px-3 py-2 text-gray-500">Ad</th>
                          <th className="text-right px-3 py-2 text-gray-500">Miktar</th>
                          <th className="text-right px-3 py-2 text-gray-500">Fiyat</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {poVendorRows.map(r => (
                          <tr key={r.rowKey}>
                            <td className="px-3 py-2"><input type="checkbox" checked={poSelectedKeys.has(r.rowKey)} onChange={() => togglePoRow(r.rowKey)} /></td>
                            <td className="px-3 py-2 font-mono text-gray-600">{r.product_code}</td>
                            <td className="px-3 py-2 text-gray-800">{r.product_name}</td>
                            <td className="px-3 py-2 text-right text-gray-800">
                              {poOrderQuantity(r)} {r.unit}
                              {poOrderQuantity(r) > r.quantity_required && <div className="text-xs text-amber-600">Gerekli {r.quantity_required} · MOQ {r.moq}</div>}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-800">{money(r.best_unit_price, r.best_currency, 4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Alıcı E-posta *</label>
                <input required type="email" value={poForm.to} onChange={e => setPoForm({...poForm, to: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj (isteğe bağlı)</label>
                <textarea value={poForm.message} onChange={e => setPoForm({...poForm, message: e.target.value})} className="w-full border rounded px-3 py-2" rows={3} />
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" disabled={sendingPo || poSelectedKeys.size === 0} className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                  {sendingPo ? 'Gönderiliyor...' : 'Gönder'}
                </button>
                <button type="button" onClick={() => setShowPoModal(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
